import { useState, useEffect } from "react";
import { useHttpClient } from "../../../../../shared/hooks/http-hook";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import MultiSelect from "./MultiSelect";
import "./ModalFiltros.scss";

const FORMAS_PAGAMENTO = [
  "Todos",
  "Dinheiro",
  "Cheque",
  "Cheque Visado",
  "Transferência de crédito",
  "Internet banking",
  "Cartão do banco",
  "Débito direto da conta bancária",
  "Cartão de crédito",
  "Cartão de débito",
];

function ModalFiltros({
  openModalFiltros,
  handleCloseModalFiltros,
  handleFiltrar,
  despesas,
  entradas,
  parcelas,
  filtrosAplicados,
  filtroCaixa,
  filtroCategoria,
  filtroMetodoPagamento,
  filtroBeneficio,
  clinicaId,
  auth,
  instituicoes,
}) {
  const beneficios = [
    "Todos",
    "Particular",
    ...instituicoes.map((i) => i.nome),
  ];

  /* Caixa, categoria e método passaram de escolha única a múltipla.
   *
   * ─── LISTA VAZIA = TODOS ────────────────────────────────────────────────
   *
   * A mesma convenção do «Recebido/Pago por», que já era multi-select, e a que
   * o próprio `MultiSelect` documenta. Vazio significa «não filtrar por isto» —
   * e não «não mostrar nada», que é onde estas coisas costumam correr mal.
   *
   * O `Array.isArray` no valor inicial é para as filtragens guardadas de uma
   * sessão anterior, que ainda podem vir como texto: sem ele, reabrir o modal
   * depois de atualizar punha uma cadeia de caracteres onde se espera um array,
   * e o `.includes` passava a comparar letras. */
  const comoLista = (valor) => (Array.isArray(valor) ? valor : []);

  // Estados
  const [categoria, setCategoria] = useState(comoLista(filtroCategoria));
  const [categorias, setCategorias] = useState([]);
  const [caixa, setCaixa] = useState(comoLista(filtroCaixa));
  const [caixas, setCaixas] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [users, setUsers] = useState([]);
  // Unidades da clínica e as escolhidas no filtro.
  //
  // Arranca VAZIO, que significa «todas as minhas unidades». O ecrã mostra tudo
  // aquilo a que o utilizador tem acesso e é ele que estreita se quiser — o que
  // está a ser mostrado aparece escrito no topo do ecrã, para não haver dúvidas.
  //
  // Antes arrancava com a unidade da sessão, mas essa escolha vivia só aqui
  // dentro e a lista do ecrã continuava a mostrar tudo até alguém carregar em
  // «Aplicar». Ou seja: o filtro dizia uma coisa e o ecrã mostrava outra.
  //
  // O servidor envia tudo o que o utilizador PODE ver; esta escolha só estreita
  // sobre isso. Tem de ser assim, porque os filtros deste modal são aplicados
  // no cliente: se o servidor mandasse só a unidade da sessão, escolher a outra
  // aqui daria sempre vazio.
  const [tipoEntrada, setTipoEntrada] = useState("todos");

  const [mistos, setMistos] = useState(
    filtrosAplicados.includes("mistos") ? true : false,
  );
  const [naoMistos, setNaoMistos] = useState(
    filtrosAplicados.includes("naoMistos") ? true : false,
  );

  const [emitidos, setEmitidos] = useState(
    filtrosAplicados.includes("emitidos") ? true : false,
  );
  const [naoEmitidos, setNaoEmitidos] = useState(
    filtrosAplicados.includes("naoEmitidos") ? true : false,
  );

  const [comReciboInterno, setComReciboInterno] = useState(
    filtrosAplicados.includes("comReciboInterno") ? true : false,
  );
  const [semReciboInterno, setSemReciboInterno] = useState(
    filtrosAplicados.includes("semReciboInterno") ? true : false,
  );

  const [radioDespesas, setRadioDespesas] = useState(
    filtrosAplicados.includes("despesas") ? true : false,
  );
  const [radioParcelas, setRadioParcelas] = useState(
    filtrosAplicados.includes("parcelas") ? true : false,
  );
  const [radioEntradas, setRadioEntradas] = useState(
    filtrosAplicados.includes("entradas") ? true : false,
  );

  const [metodoPagamento, setMetodoPagamento] = useState(
    comoLista(filtroMetodoPagamento),
  );
  const [beneficio, setBeneficio] = useState(
    filtroBeneficio ? filtroBeneficio : "Todos",
  );

  const { sendRequest } = useHttpClient();

  // Lógica para determinar quais filtros mostrar
  const nothingSelected = !radioParcelas && !radioEntradas && !radioDespesas;

  // Determinar visibilidade dos filtros
  const shouldShowCobertura = nothingSelected || radioParcelas;
  const shouldShowOrcamentosMistos = nothingSelected || radioParcelas;
  const shouldShowCategoria = nothingSelected || radioEntradas || radioDespesas;
  const shouldShowTipoEntrada = nothingSelected || radioEntradas;
  const shouldShowEmitidos = nothingSelected || radioParcelas || radioEntradas;
  const shouldShowReciboInterno =
    nothingSelected || radioParcelas || radioEntradas;

  // Fetch inicial de dados
  useEffect(() => {
    const fetchCategorias = async () => {
      try {
        const responseData = await sendRequest(
          `${process.env.REACT_APP_BACKEND_LINK}/api/categoriasfinanceiro/clinica/${clinicaId}`,
          "GET",
          null,
          { Authorization: "Bearer " + auth.token },
        );
        setCategorias(
          responseData.categoriasFinanceiro.map((cat) => {
            return { id: cat.id, value: cat.categoria };
          }),
        );
      } catch (err) {
        console.error("err", err);
      }
    };

    const fetchCaixas = async () => {
      try {
        const responseData = await sendRequest(
          `${process.env.REACT_APP_BACKEND_LINK}/api/caixas/clinica/${clinicaId}?todas=true`,
          "GET",
          null,
          { Authorization: "Bearer " + auth.token },
        );
        setCaixas(
          responseData.caixas.map((cai) => {
            return { id: cai.id, value: cai.caixa };
          }),
        );
      } catch (err) {
        console.error("err", err);
      }
    };

    const fetchUsers = async () => {
      try {
        const responseData = await sendRequest(
          `${process.env.REACT_APP_BACKEND_LINK}/api/users/ativos/${clinicaId}`,
          "GET",
          null,
          { Authorization: "Bearer " + auth.token },
        );
        setUsers(
          responseData.users.map((u) => {
            return { id: u.id, value: u.name };
          }),
        );
      } catch (err) {
        console.error("err", err);
      }
    };

    fetchUsers();
    fetchCaixas();
    fetchCategorias();
  }, [auth.token, clinicaId, sendRequest]);

  // Handlers
  const metodoPagamentoChangeHandler = (valor) => {
    setMetodoPagamento(valor);
  };

  const beneficioChangeHandler = (valor) => {
    setBeneficio(valor);
  };
  // Lógica principal de filtros (mantida do original)
  /* A unidade SAIU daqui em 2026-08-16 e passou para o seletor do topo do
     ecrã, que refaz o pedido ao servidor.

     A razão é que este modal filtra o que JÁ veio: para o ecrã abrir na
     unidade da sessão, era preciso trazer as duas unidades e esconder metade —
     pagar a rede a dobrar para deitar fora. E enquanto a escolha vivia aqui
     dentro, ninguém sabia o que estava a ver sem abrir o modal.

     Os restantes filtros continuam a filtrar no cliente, porque são recortes
     do mesmo conjunto. A unidade não é um recorte: é o conjunto. */

  /** Lista vazia não filtra; com valores, o registo tem de ser um deles. */
  const corresponde = (selecionados, valor) =>
    selecionados.length === 0 || selecionados.includes(valor);

  /* Nenhum destes três escolhido.
   *
   * Preserva um comportamento que já existia e não é óbvio: SEM filtros de
   * caixa, categoria e método, as despesas e entradas POR PAGAR também entram;
   * com qualquer um deles escolhido, só entram as pagas — uma despesa por pagar
   * não tem caixa nem método, logo não podia corresponder a nada. */
  const semFiltroDeLinha =
    caixa.length === 0 &&
    categoria.length === 0 &&
    metodoPagamento.length === 0;

  const filtrarModalHandler = () => {
    // Contas Correntes

    let parcelasFiltradas = [];
    if (nothingSelected || radioParcelas) {
      parcelasFiltradas = parcelas.filter((parcela) => {
        if (!corresponde(caixa, parcela.caixa?._id)) return false;
        if (!corresponde(metodoPagamento, parcela.metodoPagamento))
          return false;

        if (
          selectedUsers.length > 0 &&
          !selectedUsers.includes(parcela.recebidoPor?._id)
        )
          return false;

        if (
          beneficio !== "Todos" &&
          parcela.conta.orcamento.beneficio !== beneficio
        )
          return false;

        if (
          (comReciboInterno && !parcela.nrRecibo) ||
          (semReciboInterno && parcela.nrRecibo)
        )
          return false;

        if (
          (emitidos && parcela.documentoEletronico?.succeeded !== true) ||
          (naoEmitidos && parcela.documentoEletronico?.succeeded === true)
        )
          return false;

        if (
          (mistos && !parcela.conta.orcamento.orcamentoMisto) ||
          (naoMistos && parcela.conta.orcamento.orcamentoMisto)
        )
          return false;

        return true;
      });
    }

    // Despesas
    let despesasFiltradas = [];
    if (
      beneficio === "Todos" && // ✓ Só processa se não há filtro de benefício
      (nothingSelected || radioDespesas)
    ) {
      despesas.forEach((despesa) => {
        if (
          (despesa.despesaPaga &&
            corresponde(caixa, despesa.caixa?._id) &&
            corresponde(metodoPagamento, despesa.metodoPagamento) &&
            corresponde(categoria, despesa.categoria?._id)) ||
          semFiltroDeLinha
        ) {
          if (
            selectedUsers.length === 0 ||
            selectedUsers.includes(despesa.pagaPor?.user?._id)
          ) {
            despesasFiltradas.push({ ...despesa });
          }
        }
      });
    } else {
      despesasFiltradas = [];
    }

    // Entradas
    let entradasFiltradas = [];
    let todasEntradasFiltradas = [];

    if (
      beneficio === "Todos" && // ✓ Só processa se não há filtro de benefício
      (nothingSelected || radioEntradas)
    ) {
      entradas.forEach((entrada) => {
        if (
          (entrada.entradaRecebida &&
            corresponde(caixa, entrada.caixa?._id) &&
            corresponde(metodoPagamento, entrada.metodoPagamento) &&
            corresponde(categoria, entrada.categoria?._id)) ||
          semFiltroDeLinha
        ) {
          if (
            selectedUsers.length === 0 ||
            selectedUsers.includes(entrada.recebidoPor?._id)
          ) {
            if (tipoEntrada === "todos" || entrada.tipo === tipoEntrada) {
              if (
                (comReciboInterno && entrada.nrRecibo) ||
                (semReciboInterno && !entrada.nrRecibo) ||
                (!comReciboInterno && !semReciboInterno)
              ) {
                if (
                  (emitidos &&
                    entrada.documentoEletronico?.succeeded === true) ||
                  (naoEmitidos &&
                    (!entrada.documentoEletronico ||
                      entrada.documentoEletronico?.succeeded === false)) ||
                  (!emitidos && !naoEmitidos)
                ) {
                  entradasFiltradas.push({ ...entrada });
                }
              }
            }
            todasEntradasFiltradas.push({ ...entrada });
          }
        }
      });
    } else {
      entradasFiltradas = [];
    }

    const filtrosApli = [];
    mistos && filtrosApli.push("mistos");
    naoMistos && filtrosApli.push("naoMistos");
    radioDespesas && filtrosApli.push("despesas");
    radioEntradas && filtrosApli.push("entradas");
    radioParcelas && filtrosApli.push("parcelas");

    emitidos && filtrosApli.push("emitidos");
    naoEmitidos && filtrosApli.push("naoEmitidos");
    comReciboInterno && filtrosApli.push("comReciboInterno");
    semReciboInterno && filtrosApli.push("semReciboInterno");

    handleFiltrar(
      parcelasFiltradas,
      despesasFiltradas,
      entradasFiltradas,
      filtrosApli,
      caixa,
      categoria,
      metodoPagamento,
      beneficio,
      "modalFiltros",
      // Para o ecrã poder dizer no topo que unidades está a mostrar. Vazio
      // significa todas as do utilizador.
    );
    handleCloseModalFiltros();
  };

  const resetModalHandler = () => {
    setCategoria([]);
    setCaixa([]);
    setSelectedUsers([]);
    setTipoEntrada("todos");
    setMistos(false);
    setNaoMistos(false);
    setRadioDespesas(false);
    setRadioParcelas(false);
    setRadioEntradas(false);
    setMetodoPagamento([]);
    setBeneficio("Todos");

    handleFiltrar(
      parcelas,
      despesas,
      entradas,
      [],
      null,
      null,
      null,
      null,
      "modalFiltros",
      // Limpar volta a "todas as minhas unidades".
      [],
    );
    handleCloseModalFiltros();
  };

  return (
    <Modal
      open={openModalFiltros}
      onClose={handleCloseModalFiltros}
      aria-labelledby="modal-filtros-title"
      aria-describedby="modal-filtros-description"
    >
      <Box className="financeiro__modalFiltros__filtros-modal-box">
        <div className="financeiro__modalFiltros__modal-container">
          {/* Header */}
          <div className="financeiro__modalFiltros__modal-header">
            <div className="financeiro__modalFiltros__modal-title">
              <span className="financeiro__modalFiltros__filter-icon">🔍</span>
              <h2 id="modal-filtros-title">Filtrar por</h2>
            </div>
            <div className="financeiro__modalFiltros__header-actions">
              <button
                className="financeiro__modalFiltros__reset-btn"
                onClick={resetModalHandler}
              >
                ↻ Resetar
              </button>
              <button
                className="financeiro__modalFiltros__close-btn"
                onClick={handleCloseModalFiltros}
              >
                ×
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="financeiro__modalFiltros__modal-content">
            <div className="financeiro__modalFiltros__filters-section">
              {/* Tipos de Movimento */}
              <div className="financeiro__modalFiltros__filter-group">
                <h3 className="financeiro__modalFiltros__group-title">
                  Tipos de Movimento
                  <span className="financeiro__modalFiltros__subtitle">
                    {nothingSelected
                      ? "(mostrar tudo)"
                      : "(mostrar selecionado)"}
                  </span>
                </h3>
                <div className="financeiro__modalFiltros__checkbox-grid financeiro__modalFiltros__three-cols">
                  <label
                    className={`financeiro__modalFiltros__checkbox-card ${
                      radioParcelas ? "financeiro__modalFiltros__active" : ""
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={radioParcelas}
                      onChange={(e) => setRadioParcelas(e.target.checked)}
                    />
                    <div className="financeiro__modalFiltros__checkbox-content">
                      <div className="financeiro__modalFiltros__label-text">
                        Parcelas
                      </div>
                      <div className="financeiro__modalFiltros__desc-text">
                        Pagamentos de orçamentos
                      </div>
                    </div>
                  </label>

                  <label
                    className={`financeiro__modalFiltros__checkbox-card ${
                      radioEntradas ? "financeiro__modalFiltros__active" : ""
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={radioEntradas}
                      onChange={(e) => setRadioEntradas(e.target.checked)}
                    />
                    <div className="financeiro__modalFiltros__checkbox-content">
                      <div className="financeiro__modalFiltros__label-text">
                        Entradas
                      </div>
                      <div className="financeiro__modalFiltros__desc-text">
                        Receitas diversas
                      </div>
                    </div>
                  </label>

                  <label
                    className={`financeiro__modalFiltros__checkbox-card ${
                      radioDespesas ? "financeiro__modalFiltros__active" : ""
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={radioDespesas}
                      onChange={(e) => setRadioDespesas(e.target.checked)}
                    />
                    <div className="financeiro__modalFiltros__checkbox-content">
                      <div className="financeiro__modalFiltros__label-text">
                        Despesas
                      </div>
                      <div className="financeiro__modalFiltros__desc-text">
                        Custos operacionais
                      </div>
                    </div>
                  </label>
                </div>
              </div>

              {/* Cobertura/Benefício - Só mostra para Parcelas */}
              {shouldShowCobertura && (
                <div className="financeiro__modalFiltros__select-field">
                  <label className="financeiro__modalFiltros__select-label">
                    Cobertura
                    <span className="financeiro__modalFiltros__subtitle">
                      (só parcelas)
                    </span>
                  </label>
                  <select
                    value={beneficio}
                    onChange={(e) => beneficioChangeHandler(e.target.value)}
                    className="financeiro__modalFiltros__custom-select"
                  >
                    {beneficios.map((ben, index) => (
                      <option key={index} value={ben}>
                        {ben}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Emitidos e não emitidos */}
              {shouldShowEmitidos && (
                <div className="financeiro__modalFiltros__filter-group">
                  <h3 className="financeiro__modalFiltros__group-title">
                    DFEs
                    <span className="financeiro__modalFiltros__subtitle">
                      (Parcelas e Entradas)
                    </span>
                  </h3>
                  <div className="financeiro__modalFiltros__checkbox-grid financeiro__modalFiltros__two-cols">
                    <label
                      className={`financeiro__modalFiltros__checkbox-card ${
                        emitidos ? "financeiro__modalFiltros__active" : ""
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={emitidos}
                        onChange={(e) => setEmitidos(e.target.checked)}
                      />
                      <div className="financeiro__modalFiltros__checkbox-content">
                        <div className="financeiro__modalFiltros__label-text">
                          Emitidos
                        </div>
                        <div className="financeiro__modalFiltros__desc-text">
                          Com documento eletrónico emitido
                        </div>
                      </div>
                    </label>

                    <label
                      className={`financeiro__modalFiltros__checkbox-card ${
                        naoEmitidos ? "financeiro__modalFiltros__active" : ""
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={naoEmitidos}
                        onChange={(e) => setNaoEmitidos(e.target.checked)}
                      />
                      <div className="financeiro__modalFiltros__checkbox-content">
                        <div className="financeiro__modalFiltros__label-text">
                          Não emitido
                        </div>
                        <div className="financeiro__modalFiltros__desc-text">
                          Sem documento eletrónico emitido
                        </div>
                      </div>
                    </label>
                  </div>
                </div>
              )}

              {/* Com e Sem Recibo Interno */}
              {shouldShowReciboInterno && (
                <div className="financeiro__modalFiltros__filter-group">
                  <h3 className="financeiro__modalFiltros__group-title">
                    Recibo Interno
                    <span className="financeiro__modalFiltros__subtitle">
                      (Parcelas e Entradas)
                    </span>
                  </h3>
                  <div className="financeiro__modalFiltros__checkbox-grid financeiro__modalFiltros__two-cols">
                    <label
                      className={`financeiro__modalFiltros__checkbox-card ${
                        comReciboInterno
                          ? "financeiro__modalFiltros__active"
                          : ""
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={comReciboInterno}
                        onChange={(e) => setComReciboInterno(e.target.checked)}
                      />
                      <div className="financeiro__modalFiltros__checkbox-content">
                        <div className="financeiro__modalFiltros__label-text">
                          Com Recibo Interno
                        </div>
                        <div className="financeiro__modalFiltros__desc-text">
                          Com Recibo Interno anexado
                        </div>
                      </div>
                    </label>

                    <label
                      className={`financeiro__modalFiltros__checkbox-card ${
                        semReciboInterno
                          ? "financeiro__modalFiltros__active"
                          : ""
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={semReciboInterno}
                        onChange={(e) => setSemReciboInterno(e.target.checked)}
                      />
                      <div className="financeiro__modalFiltros__checkbox-content">
                        <div className="financeiro__modalFiltros__label-text">
                          Sem Recibo Interno
                        </div>
                        <div className="financeiro__modalFiltros__desc-text">
                          Sem recibo Interno anexado
                        </div>
                      </div>
                    </label>
                  </div>
                </div>
              )}

              {/* Orçamentos Mistos - Só mostra para Parcelas */}
              {shouldShowOrcamentosMistos && (
                <div className="financeiro__modalFiltros__filter-group">
                  <h3 className="financeiro__modalFiltros__group-title">
                    Orçamentos
                    <span className="financeiro__modalFiltros__subtitle">
                      (só parcelas)
                    </span>
                  </h3>
                  <div className="financeiro__modalFiltros__checkbox-grid financeiro__modalFiltros__two-cols">
                    <label
                      className={`financeiro__modalFiltros__checkbox-card ${
                        mistos ? "financeiro__modalFiltros__active" : ""
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={mistos}
                        onChange={(e) => setMistos(e.target.checked)}
                      />
                      <div className="financeiro__modalFiltros__checkbox-content">
                        <div className="financeiro__modalFiltros__label-text">
                          Orçamentos Mistos
                        </div>
                        <div className="financeiro__modalFiltros__desc-text">
                          Combinação de tratamentos com coberturas diferentes
                        </div>
                      </div>
                    </label>

                    <label
                      className={`financeiro__modalFiltros__checkbox-card ${
                        naoMistos ? "financeiro__modalFiltros__active" : ""
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={naoMistos}
                        onChange={(e) => setNaoMistos(e.target.checked)}
                      />
                      <div className="financeiro__modalFiltros__checkbox-content">
                        <div className="financeiro__modalFiltros__label-text">
                          Orçamentos não Mistos
                        </div>
                        <div className="financeiro__modalFiltros__desc-text">
                          Apenas um tipo de cobertura por orçamento
                        </div>
                      </div>
                    </label>
                  </div>
                </div>
              )}

              {/* Selects */}
              <div className="financeiro__modalFiltros__selects-grid">
                {/* Caixa - Sempre visível */}
                {caixas && (
                  <div className="financeiro__modalFiltros__select-field">
                    <label className="financeiro__modalFiltros__select-label">
                      Caixa
                    </label>
                    <MultiSelect
                      options={caixas}
                      selectedIds={caixa}
                      onChange={setCaixa}
                      placeholder="Todas"
                    />
                  </div>
                )}

                {/* Método de Pagamento - Sempre visível */}
                <div className="financeiro__modalFiltros__select-field">
                  <label className="financeiro__modalFiltros__select-label">
                    Método de pagamento
                  </label>
                  {/* O «Todos» sai da lista: aqui ele é a ausência de escolha,
                      e não mais uma opção a par das outras — deixá-lo era ter
                      duas formas de dizer a mesma coisa, uma delas capaz de
                      ficar marcada ao lado de «Dinheiro». */}
                  <MultiSelect
                    options={FORMAS_PAGAMENTO.filter((f) => f !== "Todos").map(
                      (f) => ({ id: f, value: f }),
                    )}
                    selectedIds={metodoPagamento}
                    onChange={metodoPagamentoChangeHandler}
                    placeholder="Todos"
                  />
                </div>

                {/* A unidade saiu daqui para o seletor do topo do ecrã, que
                    refaz o pedido ao servidor em vez de cortar o que já veio.
                    Ver a nota em `filtrarModalHandler`. */}

                {/* Recebido/Pago por - Sempre visível */}
                {users && (
                  <div className="financeiro__modalFiltros__select-field">
                    <label className="financeiro__modalFiltros__select-label">
                      Recebido/Pago por
                    </label>
                    <MultiSelect
                      options={users}
                      selectedIds={selectedUsers}
                      onChange={setSelectedUsers}
                      placeholder="Todos"
                    />
                  </div>
                )}

                {/* Categoria - Mostra para Entradas e Despesas */}
                {shouldShowCategoria && categorias && (
                  <div className="financeiro__modalFiltros__select-field">
                    <label className="financeiro__modalFiltros__select-label">
                      Categoria
                      <span className="financeiro__modalFiltros__subtitle">
                        (entradas e despesas)
                      </span>
                    </label>
                    <MultiSelect
                      options={categorias}
                      selectedIds={categoria}
                      onChange={setCategoria}
                      placeholder="Todas"
                    />
                  </div>
                )}

                {/* Tipo Entrada - Só mostra para Entradas */}
                {shouldShowTipoEntrada &&
                  (radioEntradas || nothingSelected) && (
                    <div className="financeiro__modalFiltros__select-field">
                      <label className="financeiro__modalFiltros__select-label">
                        Tipo Entrada
                        <span className="financeiro__modalFiltros__subtitle">
                          (só entradas)
                        </span>
                      </label>
                      <select
                        onChange={(e) => setTipoEntrada(e.target.value)}
                        className="financeiro__modalFiltros__custom-select"
                        value={tipoEntrada}
                      >
                        <option value="todos">Todos</option>
                        <option value="reembolso">Cliente</option>
                        <option value="clienteFornecedor">Fornecedor</option>
                      </select>
                    </div>
                  )}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="financeiro__modalFiltros__modal-footer">
            <button
              className="financeiro__modalFiltros__cancel-btn"
              onClick={handleCloseModalFiltros}
            >
              Fechar
            </button>
            <button
              className="financeiro__modalFiltros__apply-btn"
              onClick={filtrarModalHandler}
            >
              Filtrar
            </button>
          </div>
        </div>
      </Box>
    </Modal>
  );
}

export default ModalFiltros;
